import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Marketplace Pulse — a subtle, premium "alive feeling" line surfaced on
 * Home above the feed.
 *
 *   "12 new ads today · 5 sellers active · Just posted"
 *   "12 إعلان جديد اليوم · 5 بائعين نشطين · بُث للتو"
 *
 * Strict rules:
 *   - Only renders parts with a real, non-zero number. If every field is
 *     0/null, the entire component renders nothing — never a dead line.
 *   - Reads from `/api/marketplace/pulse` (cached 60s server-side).
 *   - One mount fires one fetch; auto-refreshes every 90s while mounted.
 *   - Mobile-first, no extra DOM nodes when hidden.
 */
export default function MarketplacePulse() {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.resolvedLanguage || i18n.language || '').startsWith('ar');
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    let timer;
    const fetchPulse = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/pulse`);
        if (!res.ok) return;
        const json = await res.json();
        if (mounted) setData(json);
      } catch {
        // Silent — pulse is non-critical surface.
      }
    };
    fetchPulse();
    timer = setInterval(fetchPulse, 90_000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);

  const parts = useMemo(() => {
    if (!data) return [];
    const out = [];
    const newToday = Number(data.new_ads_today) || 0;
    const active = Number(data.sellers_active_now) || 0;
    const ago = data.last_post_ago_min;

    if (newToday > 0) {
      out.push(isRTL
        ? `${newToday} ${newToday === 1 ? 'إعلان' : 'إعلان'} جديد اليوم`
        : `${newToday} new ${newToday === 1 ? 'ad' : 'ads'} today`);
    }
    if (active > 0) {
      out.push(isRTL
        ? `${active} ${active === 1 ? 'بائع نشط' : 'بائعين نشطين'}`
        : `${active} ${active === 1 ? 'seller' : 'sellers'} active`);
    }
    // Only surface "just posted" if the last post is < 60 minutes old.
    if (typeof ago === 'number' && ago >= 0 && ago < 60) {
      out.push(isRTL
        ? (ago === 0 ? 'بُث للتو' : `منذ ${ago} دقيقة`)
        : (ago === 0 ? 'just now' : `${ago} min ago`));
    }
    return out;
  }, [data, isRTL]);

  if (parts.length === 0) return null;

  return (
    <div
      className="mkt-pulse"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="marketplace-pulse"
      aria-live="polite"
    >
      <span className="mkt-pulse-dot" aria-hidden="true" />
      <span className="mkt-pulse-text">
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="mkt-pulse-sep" aria-hidden="true"> · </span>}
            <span data-testid={`mkt-pulse-part-${i}`}>{p}</span>
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}
